import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../redux/network/https";
import { endPoints, queryKeys } from "../../redux/network/constant";
import type { FileAttachment } from "./fileAttachments";
import type {
  ApiResponse,
  Task,
  GanttTask,
  TaskStatus,
  TaskPriority,
  Project,
} from "./projects";

// ── Param / Filter Types ─────────────────────────────────────────────────────

export interface ProjectTasksParams {
  search?: string;
  ordering?: string;
  tags_filter?: string;
  page_pending?: number;
  page_size_pending?: number;
  page_started?: number;
  page_size_started?: number;
  page_done?: number;
  page_size_done?: number;
}

export interface GanttParams {
  start?: string;
  end?: string;
  assignees?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  tags?: string;
  search?: string;
}

export interface CrossProjectGanttParams extends GanttParams {
  projects?: string;
}

// ── Response Types ───────────────────────────────────────────────────────────

export interface TaskColumnPage {
  items: Task[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ProjectTasksResponse {
  pending: TaskColumnPage;
  active: TaskColumnPage;
  done: TaskColumnPage;
}

// ── Comment Types ────────────────────────────────────────────────────────────

export interface TaskComment {
  id: number;
  task: number;
  author: number;
  author_name: string;
  body: string;
  message_type: string;
  attachments?: FileAttachment[];
  permissions?: {
    can_attach: boolean;
    can_detach: boolean;
  };
  created_at: string;
  updated_at: string;
}

export interface CreateTaskCommentPayload {
  body: string;
  message_type?: string;
}

// ── Mutation Payloads ────────────────────────────────────────────────────────

export interface UpdateTaskPayload {
  status?: TaskStatus;
  title?: string;
  description?: string;
  priority?: TaskPriority;
  start_date?: string | null;
  due_date?: string | null;
  color?: string | null;
  tags?: string[];
  blocked_by?: number[];
  assignees?: number[];
}

export interface CreateTaskPayload {
  title: string;
  description?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  start_date?: string | null;
  due_date?: string | null;
  color?: string | null;
  tags?: string[];
  blocked_by?: number[];
  assignees?: number[];
}

export interface UpdateProjectPayload {
  title?: string;
  description?: string;
  status?: string;
  start_date?: string | null;
  end_date?: string | null;
  header_gradient?: string | null;
  client_id?: number | null;
}

// ── Rollback helper ──────────────────────────────────────────────────────────

interface Rollback {
  key: readonly unknown[];
  prev: ProjectTasksResponse;
}

interface RollbackContext {
  rollbacks: Rollback[];
}

// ── Project Tasks ────────────────────────────────────────────────────────────

const fetchProjectTasks = async ({
  queryKey,
}: {
  queryKey: readonly unknown[];
}): Promise<ProjectTasksResponse> => {
  const [, , projectId, params] = queryKey as [
    string,
    string,
    number,
    ProjectTasksParams | undefined,
  ];
  const {
    search,
    ordering,
    tags_filter,
    page_pending = 1,
    page_size_pending = 20,
    page_started = 1,
    page_size_started = 20,
    page_done = 1,
    page_size_done = 20,
  } = params || {};

  const qs = new URLSearchParams();
  if (search) qs.append("search", search);
  if (ordering) qs.append("ordering", ordering);
  if (tags_filter) qs.append("tags_filter", tags_filter);
  qs.append("page_pending", String(page_pending));
  qs.append("page_size_pending", String(page_size_pending));
  qs.append("page_started", String(page_started));
  qs.append("page_size_started", String(page_size_started));
  qs.append("page_done", String(page_done));
  qs.append("page_size_done", String(page_size_done));

  let url = endPoints.ProjectTasksList(projectId);

  const paramsStr = qs.toString();
  if (paramsStr) url += `?${paramsStr}`;

  const { data } = await axiosInstance.get<ApiResponse<ProjectTasksResponse>>(url);
  return data?.data;
};

export const useProjectTasks = (
  projectId: number | null,
  params?: ProjectTasksParams,
  enabled = true,
) => {
  return useQuery({
    queryKey: queryKeys.ProjectTasks(projectId, params),
    queryFn: fetchProjectTasks,
    enabled: !!projectId && enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: (prev: ProjectTasksResponse | undefined) => prev,
  });
};

// ── Project Details ──────────────────────────────────────────────────────────

const fetchProjectDetails = async ({
  queryKey,
}: {
  queryKey: readonly unknown[];
}): Promise<Project> => {
  const [, , projectId] = queryKey as [string, string, number];
  const { data } = await axiosInstance.get<ApiResponse<Project>>(
    endPoints.ProjectDetails(projectId),
  );
  return data?.data;
};

export const useProjectDetails = (projectId: number | null, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.ProjectDetail(projectId),
    queryFn: fetchProjectDetails,
    enabled: !!projectId && enabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

// ── Update Project ───────────────────────────────────────────────────────────

const patchProjectDetails = async ({
  projectId,
  payload,
}: {
  projectId: number;
  payload: UpdateProjectPayload;
}): Promise<Project> => {
  const { data } = await axiosInstance.patch<ApiResponse<Project>>(
    endPoints.ProjectDetails(projectId),
    payload,
  );
  return data?.data;
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchProjectDetails,
    onSuccess: (_data: Project, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProjectDetail(variables?.projectId),
      });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });
};

// ── Update Task Status ───────────────────────────────────────────────────────

const patchTaskStatus = async ({
  projectId,
  taskId,
  payload,
}: {
  projectId: number;
  taskId: number;
  payload: UpdateTaskPayload;
}): Promise<Task> => {
  const { data } = await axiosInstance.patch<ApiResponse<Task>>(
    endPoints.UpdateTaskStatus(projectId, taskId),
    payload,
  );
  return data?.data;
};

export const useUpdateTaskStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchTaskStatus,
    onMutate: async (variables): Promise<RollbackContext | undefined> => {
      const projectId = variables?.projectId;
      const taskId = Number(variables?.taskId);
      const status = variables?.payload?.status;
      if (!projectId || !taskId || !status) return;
      await queryClient.cancelQueries({
        queryKey: queryKeys.ProjectTasksBase(projectId),
      });
      const matches = queryClient.getQueriesData<ProjectTasksResponse>({
        queryKey: queryKeys.ProjectTasksBase(projectId),
      });
      const rollbacks: Rollback[] = [];
      for (const [key, data] of matches) {
        if (!data) continue;
        const prev = data;
        const next = { ...prev };
        const colForStatus = (s: string): keyof ProjectTasksResponse =>
          s === "PENDING" ? "pending" : s === "ACTIVE" ? "active" : "done";
        const cols: (keyof ProjectTasksResponse)[] = ["pending", "active", "done"];
        let movedTask: Task | null = null;
        for (const c of cols) {
          const items = Array.isArray(next?.[c]?.items) ? next[c].items : [];
          const idx = items.findIndex((t) => Number(t?.id) === taskId);
          if (idx !== -1) {
            movedTask = items[idx];
            next[c] = {
              ...(next[c] || {}),
              items: items.filter((_: Task, i: number) => i !== idx),
            };
            break;
          }
        }
        const targetCol = colForStatus(status);
        if (movedTask) {
          const targetItems = Array.isArray(next?.[targetCol]?.items)
            ? next[targetCol].items
            : [];
          const updatedTask = { ...movedTask, status };
          next[targetCol] = {
            ...(next[targetCol] || {}),
            items: [...targetItems, updatedTask],
          };
        }
        rollbacks.push({ key, prev });
        queryClient.setQueryData(key, next);
      }
      return { rollbacks };
    },
    onError: (_err: unknown, _variables, context: RollbackContext | undefined) => {
      const rollbacks = context?.rollbacks || [];
      for (const rb of rollbacks) {
        queryClient.setQueryData(rb.key, rb.prev);
      }
    },
    onSuccess: (_data: Task, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProjectTasksBase(variables?.projectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.GanttTasksBase(variables?.projectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.TaskDetail(variables?.projectId, variables?.taskId),
      });
    },
  });
};

// ── Create Task ──────────────────────────────────────────────────────────────

const postCreateTask = async ({
  projectId,
  payload,
}: {
  projectId: number;
  payload: CreateTaskPayload;
}): Promise<Task> => {
  const { data } = await axiosInstance.post<ApiResponse<Task>>(
    endPoints.CreateTask(projectId),
    payload,
  );
  return data?.data;
};

export const useCreateTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postCreateTask,
    onSuccess: (_data: Task, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProjectTasksBase(variables?.projectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.GanttTasksBase(variables?.projectId),
      });
    },
  });
};

// ── Swap Task Order ──────────────────────────────────────────────────────────

const swapTaskOrder = async ({
  projectId,
  first_id,
  second_id,
}: {
  projectId: number;
  first_id: number;
  second_id: number;
}): Promise<void> => {
  const { data } = await axiosInstance.patch<ApiResponse<void>>(
    endPoints.SwapTaskOrder(projectId),
    { first_id, second_id },
  );
  return data?.data;
};

export const useSwapTaskOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: swapTaskOrder,
    onMutate: async (variables): Promise<RollbackContext | undefined> => {
      const projectId = variables?.projectId;
      const firstId = Number(variables?.first_id);
      const secondId = Number(variables?.second_id);
      if (!projectId || !firstId || !secondId) return;
      await queryClient.cancelQueries({
        queryKey: ["projects", "tasks", projectId],
      });
      const matches = queryClient.getQueriesData<ProjectTasksResponse>({
        queryKey: ["projects", "tasks", projectId],
      });
      const rollbacks: Rollback[] = [];
      for (const [key, data] of matches) {
        if (!data) continue;
        const prev = data;
        const next = { ...prev };
        const cols: (keyof ProjectTasksResponse)[] = ["pending", "active", "done"];
        for (const c of cols) {
          const items = Array.isArray(next?.[c]?.items) ? next[c].items : [];
          const i1 = items.findIndex((t) => Number(t?.id) === firstId);
          const i2 = items.findIndex((t) => Number(t?.id) === secondId);
          if (i1 !== -1 && i2 !== -1) {
            const newItems = items.slice();
            const tmp = newItems[i1];
            newItems[i1] = newItems[i2];
            newItems[i2] = tmp;
            next[c] = { ...(next[c] || {}), items: newItems };
            break;
          }
        }
        rollbacks.push({ key, prev });
        queryClient.setQueryData(key, next);
      }
      return { rollbacks };
    },
    onError: (_err: unknown, _variables, context: RollbackContext | undefined) => {
      const rollbacks = context?.rollbacks || [];
      for (const rb of rollbacks) {
        queryClient.setQueryData(rb.key, rb.prev);
      }
    },
    onSuccess: (_data: void, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["projects", "tasks", variables?.projectId],
      });
    },
  });
};

// ── Task Details ─────────────────────────────────────────────────────────────

const fetchTaskDetails = async ({
  queryKey,
}: {
  queryKey: readonly unknown[];
}): Promise<Task> => {
  const [, , projectId, taskId] = queryKey as [string, string, number, number];
  const { data } = await axiosInstance.get<ApiResponse<Task>>(
    endPoints.TaskDetails(projectId, taskId),
  );
  return data?.data;
};

export const useTaskDetails = (
  projectId: number | null,
  taskId: number | null,
  enabled = true,
) => {
  return useQuery({
    queryKey: queryKeys.TaskDetail(projectId, taskId),
    queryFn: fetchTaskDetails,
    enabled: !!projectId && !!taskId && enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

// ── Gantt Tasks ──────────────────────────────────────────────────────────────

const fetchGanttTasks = async ({
  queryKey,
}: {
  queryKey: readonly unknown[];
}): Promise<Task[]> => {
  const [, , projectId, params] = queryKey as [
    string,
    string,
    number,
    GanttParams | undefined,
  ];
  const qs = new URLSearchParams();
  if (params?.start) qs.append("start", params.start);
  if (params?.end) qs.append("end", params.end);
  if (params?.assignees) qs.append("assignees", params.assignees);
  if (params?.status) qs.append("status", params.status);
  if (params?.priority) qs.append("priority", params.priority);
  if (params?.tags) qs.append("tags", params.tags);
  if (params?.search) qs.append("search", params.search);
  let url = endPoints.GanttTasks(projectId);
  const str = qs.toString();
  if (str) url += `?${str}`;
  const { data } = await axiosInstance.get<ApiResponse<Task[]>>(url);
  return data?.data;
};

export const useGanttTasks = (
  projectId: number | null,
  params?: GanttParams,
  enabled = true,
) => {
  return useQuery({
    queryKey: queryKeys.GanttTasks(projectId, params),
    queryFn: fetchGanttTasks,
    enabled: !!projectId && enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    placeholderData: (prev: Task[] | undefined) => prev,
  });
};

// ── Cross-Project Gantt Tasks ────────────────────────────────────────────────

const fetchCrossProjectGanttTasks = async ({
  queryKey,
}: {
  queryKey: readonly unknown[];
}): Promise<GanttTask[]> => {
  const [, , params] = queryKey as [string, string, CrossProjectGanttParams | undefined];
  const qs = new URLSearchParams();
  if (params?.start) qs.append("start", params.start);
  if (params?.end) qs.append("end", params.end);
  if (params?.assignees) qs.append("assignees", params.assignees);
  if (params?.status) qs.append("status", params.status);
  if (params?.priority) qs.append("priority", params.priority);
  if (params?.tags) qs.append("tags", params.tags);
  if (params?.search) qs.append("search", params.search);
  if (params?.projects) qs.append("projects", params.projects);
  let url = endPoints.CrossProjectGanttTasks;
  const str = qs.toString();
  if (str) url += `?${str}`;
  const { data } = await axiosInstance.get<ApiResponse<GanttTask[]>>(url);
  return data?.data;
};

export const useCrossProjectGanttTasks = (
  params?: CrossProjectGanttParams,
  enabled = true,
) => {
  return useQuery({
    queryKey: queryKeys.CrossProjectGanttTasks(params),
    queryFn: fetchCrossProjectGanttTasks,
    enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    placeholderData: (prev: GanttTask[] | undefined) => prev,
  });
};

// ── Task Comments ────────────────────────────────────────────────────────────

const fetchTaskComments = async ({
  queryKey,
}: {
  queryKey: readonly unknown[];
}): Promise<TaskComment[]> => {
  const [, , , projectId, taskId] = queryKey as [
    string,
    string,
    string,
    number,
    number,
  ];
  const qs = new URLSearchParams();
  qs.append("page_size", "200");
  qs.append("ordering", "-created_at");
  let url = endPoints.TaskComments(projectId, taskId);
  const str = qs.toString();
  if (str) url += `?${str}`;
  const { data } = await axiosInstance.get<ApiResponse<TaskComment[]>>(url);
  return data?.data;
};

export const useTaskComments = (
  projectId: number | null,
  taskId: number | null,
  enabled = true,
) => {
  return useQuery({
    queryKey: queryKeys.TaskCommentsBase(projectId, taskId),
    queryFn: fetchTaskComments,
    enabled: !!projectId && !!taskId && enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

// ── Create Task Comment ──────────────────────────────────────────────────────

const postCreateTaskComment = async ({
  projectId,
  taskId,
  payload,
}: {
  projectId: number;
  taskId: number;
  payload: CreateTaskCommentPayload;
}): Promise<TaskComment> => {
  const { data } = await axiosInstance.post<ApiResponse<TaskComment>>(
    endPoints.TaskComments(projectId, taskId),
    payload,
  );
  return data?.data;
};

export const useCreateTaskComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postCreateTaskComment,
    onSuccess: (_data: TaskComment, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.TaskCommentsBase(
          variables?.projectId,
          variables?.taskId,
        ),
      });
    },
  });
};
